import { useEffect, useState } from "react"
import { Link } from "react-router-dom"
import { Chart, registerables } from "chart.js"
import { Pie } from "react-chartjs-2"
import { fetchData } from "../../api/Axios"
import { formatDate, formatDuration, weekComparisonRows } from "./activity"

Chart.register(...registerables)

const CATEGORIES = [
    { key: "new", label: "Neue Spieler", hint: "erste Sitzung in dieser Woche" },
    { key: "regular", label: "Reguläre Spieler", hint: "nicht neu, genug Spielzeit, nicht inaktiv" },
    { key: "active", label: "Aktive Spieler", hint: "in dieser Woche über der Aktiv-Schwelle" },
    { key: "inactive", label: "Inaktive Spieler", hint: "lange keine Sitzung, war aber einmal aktiv" },
    { key: "never_active", label: "Nie aktiv", hint: "insgesamt unter der Nie-aktiv-Schwelle" },
]

const DISTRIBUTION_COLORS = ["#d2d6de", "#00c0ef", "#3c8dbc", "#00a65a"]

// Spieleraktivitaet (Issue #1746): Wochenvergleich, Gesamtwerte, Spielzeitverteilung und Spielerlisten.
// Die Zahlen kommen aus dem OnlineTime-Plugin (/onlinetime/playeractivity), Aktivitaeten und Quests aus /stats.
const PlayerActivity = () => {
    const [activity, setActivity] = useState(null)
    const [weekStats, setWeekStats] = useState({})
    const [overall, setOverall] = useState({})
    const [category, setCategory] = useState("new")

    useEffect(() => {
        const abortController = new AbortController()
        fetchData("/api/onlinetime/playeractivity", "Spieleraktivität konnte nicht geladen werden.", data => {
            setActivity(data)
            for (const week of data.weeks || []) {
                const range = "&from=" + week.start + "&until=" + (week.end - 1)
                fetchData("/api/stats?format=int&stats=Aktivität" + range, "Aktivitäten der Woche konnten nicht geladen werden.",
                    stats => setWeekStats(old => ({ ...old, [week.week]: { ...old[week.week], activities: stats.amount } })),
                    abortController.signal)
                fetchData("/api/stats?format=int&stats=Quest" + range, "Quests der Woche konnten nicht geladen werden.",
                    stats => setWeekStats(old => ({ ...old, [week.week]: { ...old[week.week], quests: stats.amount } })),
                    abortController.signal)
            }
        }, abortController.signal)
        fetchData("/api/stats?format=int&stats=Aktivität", "Aktivitäten konnten nicht geladen werden.",
            stats => setOverall(old => ({ ...old, activities: stats.amount })), abortController.signal)
        fetchData("/api/stats?format=int&stats=Quest", "Quests konnten nicht geladen werden.",
            stats => setOverall(old => ({ ...old, quests: stats.amount })), abortController.signal)
        return () => abortController.abort()
    }, [])

    if (activity === null) {
        return <p>Lade Spieleraktivität...</p>
    }

    const settings = activity.settings
    const weeks = activity.weeks || []
    const allTime = activity.all_time || {}
    const rows = weekComparisonRows(weeks, weekStats, settings)
    const players = (activity.players || {})[category] || []
    const show = value => value === undefined ? "…" : value

    return (
        <div className="row">
            <div className="col-12 col-lg-4">
                <div className="card card-primary">
                    <div className="card-header"><h3>Gesamt</h3></div>
                    <div className="card-body">
                        <table className="table table-sm">
                            <tbody>
                                <tr><td>Spieler insgesamt</td><td>{allTime.players}</td></tr>
                                <tr><td>Reguläre Spieler</td><td>{allTime.regular_players}</td></tr>
                                <tr><td>Neue Spieler (diese Woche)</td><td>{allTime.new_players}</td></tr>
                                <tr><td>Aktive Spieler (diese Woche)</td><td>{allTime.active_players}</td></tr>
                                <tr><td>Inaktive Spieler</td><td>{allTime.inactive_players}</td></tr>
                                <tr><td>Nie aktive Spieler</td><td>{allTime.never_active_players}</td></tr>
                                <tr><td>Erledigte Aktivitäten</td><td>{show(overall.activities)}</td></tr>
                                <tr><td>Erledigte Quests</td><td>{show(overall.quests)}</td></tr>
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
            <div className="col-12 col-lg-8">
                <div className="card card-primary">
                    <div className="card-header"><h3>Wochenvergleich</h3></div>
                    <div className="card-body">
                        <table className="table table-sm table-striped">
                            <thead>
                                <tr>
                                    <th></th>
                                    {weeks.map(week => (
                                        <th key={week.week}>{week.partial ? "Diese Woche" : "Letzte Woche"} ({week.week}{week.partial ? ", bis jetzt" : ""})</th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {rows.map(row => (
                                    <tr key={row.label}>
                                        <td>{row.label}</td>
                                        {row.values.map((value, index) => <td key={index}>{show(value)}</td>)}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                        <small className="text-muted">
                            Aktiv: ab {formatDuration(settings.active_seconds)} in der Woche. Regulär: nicht neu, ab {formatDuration(settings.regular_seconds)} insgesamt und nicht inaktiv.
                            Inaktiv: {settings.inactive_days} Tage ohne Sitzung. Nie aktiv: unter {formatDuration(settings.never_active_seconds)} insgesamt.
                            Eine Sitzung zählt beim Logout. Ø je Spieler bezieht sich auf die Spieler mit Sitzung in der Woche.
                        </small>
                    </div>
                </div>
            </div>
            <div className="col-12 col-lg-4">
                <div className="card card-primary">
                    <div className="card-header"><h3>Gesamtspielzeit der Spieler</h3></div>
                    <div className="card-body">
                        <Pie data={{
                            labels: (activity.distribution || []).map(bucket => bucket.label),
                            datasets: [{
                                data: (activity.distribution || []).map(bucket => bucket.players),
                                backgroundColor: DISTRIBUTION_COLORS,
                            }],
                        }} />
                    </div>
                </div>
            </div>
            <div className="col-12 col-lg-8">
                <div className="card card-primary">
                    <div className="card-header"><h3>Spieler nach Kategorie</h3></div>
                    <div className="card-body">
                        <div className="btn-group mb-3" role="group">
                            {CATEGORIES.map(entry => (
                                <button key={entry.key} type="button" title={entry.hint}
                                        className={"btn " + (category === entry.key ? "btn-primary" : "btn-outline-primary")}
                                        onClick={() => setCategory(entry.key)}>
                                    {entry.label} ({((activity.players || {})[entry.key] || []).length})
                                </button>
                            ))}
                        </div>
                        {players.length === 0 ? <p>Keine Spieler in dieser Kategorie.</p> :
                            <table className="table table-sm table-striped">
                                <thead>
                                    <tr><th>Spieler</th><th>Spielzeit</th><th>Erste Sitzung</th><th>Letzte Sitzung</th></tr>
                                </thead>
                                <tbody>
                                    {players.map(player => (
                                        <tr key={player.player_uuid}>
                                            <td><Link to={"/player/" + player.player_name}>{player.player_name}</Link></td>
                                            <td>{formatDuration(player.online_time)}</td>
                                            <td>{formatDate(player.first_session)}</td>
                                            <td>{formatDate(player.last_session)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>}
                    </div>
                </div>
            </div>
        </div>
    )
}

export default PlayerActivity
